import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { PreventiviActions } from './preventivi.actions';
import { PreventiviPort } from '../../core/services/preventivi/preventivi.port';
import { catchError, map, of, switchMap, withLatestFrom, tap, finalize } from 'rxjs';
import { selectLastQuery, selectPageSize } from './preventivi.selectors';
import { LoadingService } from '../../core/services/loading/loading.service';
import { SwalService } from '../../core/services/alerts/swal.service';
import { Router } from '@angular/router';
import {CapitoliActions} from '../capitoli/capitoli.actions';

@Injectable()
export class PreventiviEffects {
  private actions$ = inject(Actions);
  private api = inject(PreventiviPort);
  private store = inject(Store);
  private loading = inject(LoadingService);
  private swalService = inject(SwalService);
  private router = inject(Router);

  loadList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PreventiviActions.loadList),
      withLatestFrom(this.store.select(selectPageSize)),
      switchMap(([{ params }, pageSize]) => {
        const queryParams = {
          ...params,
          size: params?.size ?? pageSize,
          page: params?.page ?? 0, // Swagger: page (0..N)
        };

        return this.api.getList(queryParams).pipe(
          map((response) =>
            PreventiviActions.loadListSuccess({
              items: response.content,
              totalElements: response.totalElements,
              totalPages: response.totalPages,
              pageIndex: response.pageIndex,
            })
          ),
          catchError((err) =>
            of(
              PreventiviActions.loadListFailure({
                error: err?.message ?? 'Errore caricamento',
              })
            )
          )
        );
      })
    )
  );

  loadDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PreventiviActions.loadDetails),
      tap(() => this.loading.show()),
      switchMap(({ id }) =>
        this.api.getById(id).pipe(
          map((item) => PreventiviActions.loadDetailsSuccess({ item })),
          catchError((err) =>
            of(
              PreventiviActions.loadDetailsFailure({
                error: err?.message ?? 'Errore dettagli',
              })
            )
          ),
          finalize(() => this.loading.hide())
        )
      )
    )
  );

  create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PreventiviActions.create),
      tap(() => this.loading.show()),
      switchMap(({ dto }) =>
        this.api.create(dto).pipe(
          map((id) => PreventiviActions.createSuccess({ id })),
          catchError((err) =>
            of(
              PreventiviActions.createFailure({
                error: err?.message ?? 'Errore creazione',
              })
            )
          ),
          finalize(() => this.loading.hide())
        )
      )
    )
  );

  createSuccessNavigate$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PreventiviActions.createSuccess),
        tap(({ id }) => {
          this.router.navigate(['/preventivi', id]);
        })
      ),
    { dispatch: false }
  );

  update$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PreventiviActions.update),
      tap(() => this.loading.show()),
      switchMap(({ id, changes }) =>
        this.api.update(id, changes).pipe(
          map((returnedId) => PreventiviActions.updateSuccess({ id: Number(returnedId) })),
          catchError((err) =>
            of(
              PreventiviActions.updateFailure({
                error: err?.message ?? 'Errore modifica',
              })
            )
          ),
          finalize(() => this.loading.hide())
        )
      )
    )
  );

  delete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PreventiviActions.delete),
      tap(() => this.loading.show()),
      switchMap(({ id }) =>
        this.api.delete(id).pipe(
          map(() => PreventiviActions.deleteSuccess({ id })),
          catchError((err) =>
            of(
              PreventiviActions.deleteFailure({
                error: err?.message ?? 'Errore eliminazione',
              })
            )
          ),
          finalize(() => this.loading.hide())
        )
      )
    )
  );

  archive$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PreventiviActions.archive),
      tap(() => this.loading.show()),
      switchMap(({ id }) =>
        this.api.archive(id).pipe(
          map((resId) => PreventiviActions.archiveSuccess({ id: Number(resId ?? id) })),
          catchError((err) =>
            of(
              PreventiviActions.archiveFailure({
                error: err?.message ?? 'Errore archivio',
              })
            )
          ),
          finalize(() => this.loading.hide())
        )
      )
    )
  );

  unarchive$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PreventiviActions.unarchive),
      tap(() => this.loading.show()),
      switchMap(({ id }) =>
        this.api.unarchive(id).pipe(
          map((resId) => PreventiviActions.unarchiveSuccess({ id: Number(resId ?? id) })),
          catchError((err) =>
            of(
              PreventiviActions.unarchiveFailure({
                error: err?.message ?? 'Errore ripristino',
              })
            )
          ),
          finalize(() => this.loading.hide())
        )
      )
    )
  );

  setAsRefused$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PreventiviActions.setAsRefused),
      tap(() => this.loading.show()),
      switchMap(({ id, notaRifiuto , dataRifiutato }) =>
        this.api.setAsRefused(id, notaRifiuto, dataRifiutato).pipe(
          map((resId) => PreventiviActions.setAsRefusedSuccess({ id: Number(resId ?? id) })),
          catchError((err) =>
            of(
              PreventiviActions.setAsRefusedFailure({
                error: err?.message ?? 'Errore rifiuto preventivo',
              })
            )
          ),
          finalize(() => this.loading.hide())
        )
      )
    )
  );

  associateToCantiere$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PreventiviActions.associateToCantiere),
      tap(() => this.loading.show()),
      switchMap(({ id, idCantiere, dataAccettato }) =>
        this.api.associateTo(id, idCantiere, dataAccettato).pipe(
          map((resId) => PreventiviActions.associateToCantiereSuccess({ id: Number(resId ?? id) })),
          catchError((err) =>
            of(
              PreventiviActions.associateToCantiereFailure({
                error: err?.message ?? 'Errore associazione cantiere',
              })
            )
          ),
          finalize(() => this.loading.hide())
        )
      )
    )
  );

  duplicate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PreventiviActions.duplicate),
      tap(() => this.loading.show()),
      switchMap(({ id, idCliente }) =>
        this.api.duplicate(id, idCliente).pipe(
          map((newId) => PreventiviActions.duplicateSuccess({ id: Number(newId) })),
          catchError((err) =>
            of(
              PreventiviActions.duplicateFailure({
                error: err?.message ?? 'Errore duplicazione',
              })
            )
          ),
          finalize(() => this.loading.hide())
        )
      )
    )
  );

  refreshListAfterMutations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        PreventiviActions.updateSuccess,
        PreventiviActions.deleteSuccess,
        PreventiviActions.archiveSuccess,
        PreventiviActions.unarchiveSuccess,
        PreventiviActions.setAsRefusedSuccess,
        PreventiviActions.associateToCantiereSuccess
      ),
      withLatestFrom(this.store.select(selectLastQuery)),
      map(([_, lastQuery]) => PreventiviActions.loadList({ params: lastQuery }))
    )
  );

  refreshDetailsAfterMutations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        PreventiviActions.updateSuccess,
        PreventiviActions.archiveSuccess,
        PreventiviActions.unarchiveSuccess,
        PreventiviActions.setAsRefusedSuccess,
        PreventiviActions.associateToCantiereSuccess
      ),
      map(({ id }) => PreventiviActions.loadDetails({ id }))
    )
  );

  showMutationSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          PreventiviActions.createSuccess,
          PreventiviActions.updateSuccess,
          PreventiviActions.deleteSuccess,
          PreventiviActions.archiveSuccess,
          PreventiviActions.unarchiveSuccess,
          PreventiviActions.setAsRefusedSuccess,
          PreventiviActions.associateToCantiereSuccess,
          PreventiviActions.duplicateSuccess
        ),
        tap((action) => {
          switch (action.type) {
            case PreventiviActions.createSuccess.type:
              this.swalService.success('Preventivo creato con successo!');
              return;
            case PreventiviActions.updateSuccess.type:
              this.swalService.success('Preventivo aggiornato con successo!');
              return;
            case PreventiviActions.deleteSuccess.type:
              this.swalService.success('Preventivo eliminato con successo!');
              return;
            case PreventiviActions.archiveSuccess.type:
              this.swalService.success('Preventivo spostato in archivio');
              return;
            case PreventiviActions.unarchiveSuccess.type:
              this.swalService.success('Preventivo ripristinato');
              return;
            case PreventiviActions.setAsRefusedSuccess.type:
              this.swalService.success('Preventivo segnato come rifiutato');
              return;
            case PreventiviActions.associateToCantiereSuccess.type:
              this.swalService.success('Preventivo associato al cantiere');
              return;
            case PreventiviActions.duplicateSuccess.type:
              this.swalService.success('Preventivo duplicato con successo!');
              return;
          }
        })
      ),
    { dispatch: false }
  );

  showMutationFailureToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          PreventiviActions.createFailure,
          PreventiviActions.updateFailure,
          PreventiviActions.deleteFailure,
          PreventiviActions.archiveFailure,
          PreventiviActions.unarchiveFailure,
          PreventiviActions.setAsRefusedFailure,
          PreventiviActions.associateToCantiereFailure,
          PreventiviActions.duplicateFailure
        ),
        tap(({ error }) => this.swalService.error(`Errore: ${error}`))
      ),
    { dispatch: false }
  );

  navigateOnDuplicateSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PreventiviActions.duplicateSuccess),
        tap(({ id }) => this.router.navigate(['/preventivi', id]))
      ),
    { dispatch: false }
  );
  loadDocumenti$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PreventiviActions.loadDocumenti),
      switchMap(({ idPreventivo }) =>
        this.api.getDocumentiByPreventivo(idPreventivo).pipe(
          map(items => PreventiviActions.loadDocumentiSuccess({ items })),
          catchError(err =>
            of(PreventiviActions.loadDocumentiFailure({
              error: err?.message ?? 'Errore caricamento documenti',
            }))
          )
        )
      )
    )
  );

  createDocumento$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PreventiviActions.createDocumento),
      switchMap(({ idPreventivo, file, descrizione }) =>
        this.api.createDocumentoPreventivo(idPreventivo, file, descrizione).pipe(
          map(id => PreventiviActions.createDocumentoSuccess({ idPreventivo, id })),
          catchError(err =>
            of(PreventiviActions.createDocumentoFailure({
              error: err?.message ?? 'Errore creazione documento',
            }))
          )
        )
      )
    )
  );

  reloadAfterCreateDocumento$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PreventiviActions.createDocumentoSuccess),
      map(({ idPreventivo }) =>
        PreventiviActions.loadDocumenti({ idPreventivo })
      )
    )
  );

  updateDocumento$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PreventiviActions.updateDocumento),
      switchMap(({ id, idPreventivo, descrizione }) =>
        this.api.updateDocumentoPreventivo(id, descrizione).pipe(
          map(() => PreventiviActions.updateDocumentoSuccess({ idPreventivo, id })),
          catchError(err =>
            of(PreventiviActions.updateDocumentoFailure({
              error: err?.message ?? 'Errore modifica documento',
            }))
          )
        )
      )
    )
  );

  reloadAfterUpdateDocumento$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PreventiviActions.updateDocumentoSuccess),
      map(({ idPreventivo }) =>
        PreventiviActions.loadDocumenti({ idPreventivo })
      )
    )
  );

  deleteDocumento$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PreventiviActions.deleteDocumento),
      switchMap(({ id, idPreventivo }) =>
        this.api.deleteDocumentoPreventivo(id).pipe(
          map(() => PreventiviActions.deleteDocumentoSuccess({ idPreventivo, id })),
          catchError(err =>
            of(PreventiviActions.deleteDocumentoFailure({
              error: err?.message ?? 'Errore eliminazione documento',
            }))
          )
        )
      )
    )
  );

  reloadAfterDeleteDocumento$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PreventiviActions.deleteDocumentoSuccess),
      map(({ idPreventivo }) =>
        PreventiviActions.loadDocumenti({ idPreventivo })
      )
    )
  );

  previewDocumentoFile$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PreventiviActions.previewDocumentoFile),
        switchMap(({ fileId }) =>
          this.api.previewFile(fileId).pipe(
            tap((blob: Blob) => {
              const url = URL.createObjectURL(blob);
              window.open(url, '_blank');
            }),
            catchError(err => {
              console.error(err);
              return of(null);
            })
          )
        )
      ),
    { dispatch: false }
  );
  moveCapitolo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PreventiviActions.moveCapitolo),
      switchMap(({ id, idPreventivo, nuovoOrdine }) =>
        this.api.moveCapitolo(id, nuovoOrdine).pipe(
          map(() => PreventiviActions.loadCapitoli({ idPreventivo })),
          catchError(error =>
            of(PreventiviActions.moveCapitoloFailure({
              error: error?.message ?? 'Errore ordinamento capitolo',
            }))
          )
        )
      )
    )
  );

  loadCapitoli$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PreventiviActions.loadCapitoli),
      switchMap(({ idPreventivo }) =>
        this.api.getCapitoliByPreventivo(idPreventivo).pipe(
          map(items => PreventiviActions.loadCapitoliSuccess({ items })),
          catchError(error =>
            of(PreventiviActions.loadCapitoliFailure({
              error: error?.message ?? 'Errore caricamento capitoli',
            }))
          )
        )
      )
    )
  );

  // In capitoli.effects.ts, dopo createSuccess/updateSuccess/deleteSuccess:
  refreshCapitoliAfterMutation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        CapitoliActions.createSuccess,
        CapitoliActions.updateSuccess,
        CapitoliActions.deleteSuccess,
      ),
      map(({ idPreventivo }) => PreventiviActions.loadCapitoli({ idPreventivo }))
    )
  );

}
